import { supabase } from "@/integrations/supabase/client";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotifications } from "./useNotifications";
import type { Database } from "@repo/supabase/types";

export type ChatMessage = Pick<
  Database["public"]["Tables"]["chat_history"]["Row"],
  "id" | "message" | "sender" | "created_at" | "status" | "attachments"
>;

interface UseChatHistoryOptions {
  characterId?: string;
  userId?: string;
}

export const useChatHistory = ({
  characterId,
  userId,
}: UseChatHistoryOptions): UseQueryResult<ChatMessage[]> => {
  const { isEnabled: isNotificationsEnabled } = useNotifications();
  const queryClient = useQueryClient();
  const refetchInterval = isNotificationsEnabled ? 2 * 1000 : 2 * 1000;

  return useQuery<ChatMessage[]>({
    queryKey: ["chat-history", userId, characterId],
    queryFn: async () => {
      if (!characterId || !userId) return [];
      const { data, error } = await supabase
        .from("chat_history")
        .select("id, message, sender, created_at, status, attachments")
        .eq("character_id", characterId)
        .eq("profile_id", userId)
        .order("created_at", { ascending: false });
      if (error) throw error;

      try {
        // mark messages as read
        const { error: updateError } = await supabase
          .from("chat_history")
          .update({ status: "read" })
          .eq("profile_id", userId)
          .eq("character_id", characterId)
          .in(
            "id",
            data
              ?.filter(
                (row) =>
                  row.status === "delivered" && row.sender === "character"
              )
              .map((row) => row.id) ?? []
          );
        queryClient.invalidateQueries({
          queryKey: ["conversations"],
        });
        if (updateError) throw updateError;
      } catch (error) {
        console.error("Failed to mark messages as read", error);
      }

      return (
        data?.map((row) => ({
          id: row.id,
          message: row.message,
          status: row.status,
          sender: row.sender as "user" | "assistant",
          created_at: row.created_at,
          attachments: row.attachments,
        })) ?? []
      );
    },
    enabled: !!characterId && !!userId,
    refetchInterval,
    staleTime: 0,
  });
};
